import React from 'react';
import PropTypes from 'prop-types';
import { Table } from 'bootstrap-4-react/lib/components';
import ActionItems from '../../../common/showActions';
import BatchPagination from '../../../batch/BatchPagination';

// ============================================================================
// Subcomponents - Table Structure
// ============================================================================

/**
 * SortableColumnHeader - Renders a clickable sortable column header with sort indicator
 * @param {string} columnName - Display name of the column
 * @param {string} columnKey - Key used for sorting (e.g., 'invoiceNumber')
 * @param {Object} filterData - Current filter/sort state with sortColumn and sortOrder
 * @param {Function} onSortClick - Callback function called with columnKey when clicked
 */
export const SortableColumnHeader = ({ columnName, columnKey, filterData, onSortClick }) => {
    const isActive = filterData.sortColumn === columnKey;
    const isAscending = isActive && filterData.sortOrder === 'ASCENDING';

    return (
        <th className="trows-s">
            <button
                className={isActive ? 'active sortBtn' : 'sortColumn sortBtn'}
                onClick={() => onSortClick(columnKey)}
                title={`Sort by ${columnName}`}
            >
                {columnName} <i className={isAscending ? 'fa fa-arrow-up' : 'fa fa-arrow-down'} aria-hidden="true" />
            </button>
        </th>
    );
};

SortableColumnHeader.propTypes = {
    columnName: PropTypes.string.isRequired,
    columnKey: PropTypes.string.isRequired,
    filterData: PropTypes.shape({
        sortColumn: PropTypes.string,
        sortOrder: PropTypes.string,
    }).isRequired,
    onSortClick: PropTypes.func.isRequired,
};

/**
 * InvoiceTableHeader - Renders table column headers
 * Displays 6 columns: Invoice #, Company/Seller, Service Types, Invoice Date, Amount, Actions
 * @param {Object} filterData - Current filter/sort state (contains sortColumn, sortOrder)
 * @param {Function} onSortClick - Callback for sortable column clicks
 */
export const InvoiceTableHeader = ({ filterData, onSortClick }) => {
    return (
        <thead>
            <tr className="trow">
                <SortableColumnHeader
                    columnName="Invoice #"
                    columnKey="invoiceNumber"
                    filterData={filterData}
                    onSortClick={onSortClick}
                />
                <SortableColumnHeader
                    columnName="Company / Seller"
                    columnKey="companySellerName"
                    filterData={filterData}
                    onSortClick={onSortClick}
                />
                <th className="trows-s">Service Types</th>
                <SortableColumnHeader
                    columnName="Invoice Date"
                    columnKey="invoiceDate"
                    filterData={filterData}
                    onSortClick={onSortClick}
                />
                <SortableColumnHeader
                    columnName="Amount"
                    columnKey="totalAmount"
                    filterData={filterData}
                    onSortClick={onSortClick}
                />
                <th className="trows-s sticky">Actions</th>
            </tr>
        </thead>
    );
};

InvoiceTableHeader.propTypes = {
    filterData: PropTypes.shape({
        sortColumn: PropTypes.string,
        sortOrder: PropTypes.string,
    }).isRequired,
    onSortClick: PropTypes.func.isRequired,
};

/**
 * InvoiceEmptyState - Renders empty state row when no invoices exist
 * @param {number} colSpan - Number of columns to span (default: 6)
 */
export const InvoiceEmptyState = ({ colSpan = 6 }) => {
    return (
        <tr>
            <td colSpan={colSpan} align="center">
                No invoices found
            </td>
        </tr>
    );
};

InvoiceEmptyState.propTypes = {
    colSpan: PropTypes.number,
};

/**
 * InvoiceActionCell - Renders action button cell for a single invoice
 * @param {Object} invoice - Invoice data object
 * @param {Function} onAction - Callback function called with (invoice, actionType)
 * @param {Function} onDownloadPDF - Callback function for PDF download action
 * @param {boolean} isDownloading - Whether PDF is currently downloading
 */
export const InvoiceActionCell = ({ invoice, onAction, onDownloadPDF, isDownloading = false }) => {
    const handleViewClick = () => {
        onAction(invoice, 'view');
    };

    const handleDownloadClick = () => {
        if (onDownloadPDF && !isDownloading) {
            onDownloadPDF(invoice);
        }
    };

    return (
        <td className="sticky pl-4" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <ActionItems
                type={'View'}
                item={invoice}
                handleAction={handleViewClick}
                disabled={isDownloading}
            />
            <button
                className="btn btn-sm btn-outline-primary"
                onClick={handleDownloadClick}
                disabled={isDownloading}
                title="Download invoice as PDF"
                data-testid={`pdf-download-btn-${invoice.id}`}
                style={{
                    padding: '4px 12px',
                    fontSize: '12px',
                    borderRadius: '3px',
                    cursor: isDownloading ? 'not-allowed' : 'pointer',
                    opacity: isDownloading ? 0.6 : 1,
                }}
            >
                {isDownloading ? (
                    <>
                        <span
                            className="spinner-border spinner-border-sm mr-2"
                            aria-hidden="true"
                            style={{ width: '12px', height: '12px' }}
                        />
                        {' '}
                        Downloading...
                    </>
                ) : (
                    <>
                        <i className="fa fa-download mr-1" aria-hidden="true" />
                        {' '}
                        PDF
                    </>
                )}
            </button>
        </td>
    );
};

InvoiceActionCell.propTypes = {
    invoice: PropTypes.object.isRequired,
    onAction: PropTypes.func.isRequired,
    onDownloadPDF: PropTypes.func,
    isDownloading: PropTypes.bool,
};

/**
 * InvoiceTableRow - Renders a single invoice row with formatted data
 * @param {Object} invoice - Invoice data object
 * @param {Function} onAction - Callback function for row actions
 * @param {Function} onDownloadPDF - Callback function for PDF download
 * @param {boolean} isDownloading - Whether PDF is currently downloading for this invoice
 */
export const InvoiceTableRow = ({ invoice, onAction, onDownloadPDF, isDownloading = false }) => {
    return (
        <tr key={invoice.id}>
            <td className="trows-s">{invoice.invoiceNumber}</td>
            <td className="trows-s">{invoice.companyName}</td>
            <td className="trows-s">{invoice.serviceTypes}</td>
            <td className="trows-s">{invoice.invoiceDate}</td>
            <td className="trows-s">${Math.abs(invoice.amount).toFixed(2)}</td>
            <InvoiceActionCell
                invoice={invoice}
                onAction={onAction}
                onDownloadPDF={onDownloadPDF}
                isDownloading={isDownloading}
            />
        </tr>
    );
};

InvoiceTableRow.propTypes = {
    invoice: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        invoiceNumber: PropTypes.string.isRequired,
        companyName: PropTypes.string.isRequired,
        serviceTypes: PropTypes.string.isRequired,
        invoiceDate: PropTypes.string.isRequired,
        amount: PropTypes.number.isRequired,
    }).isRequired,
    onAction: PropTypes.func.isRequired,
    onDownloadPDF: PropTypes.func,
    isDownloading: PropTypes.bool,
};

/**
 * InvoiceTable - Table container rendering headers, rows, and empty state
 * @param {Array} invoiceListData - Array of invoice objects to display
 * @param {Object} filterData - Current filter/sort state (contains sortColumn, sortOrder)
 * @param {Function} onRowAction - Callback function for row actions
 * @param {Function} onSortClick - Callback function for column sort clicks
 * @param {Function} onDownloadPDF - Callback function for PDF download
 * @param {string} downloadingInvoiceId - ID of invoice currently being downloaded
 * @param {boolean} isLoading - Whether data is currently loading
 */
export const InvoiceTable = ({
    invoiceListData,
    filterData,
    onRowAction,
    onSortClick,
    onDownloadPDF,
    downloadingInvoiceId,
    isLoading = false,
}) => {
    const shouldShowRows = invoiceListData && invoiceListData.length > 0 && !isLoading;
    const showEmptyState = !shouldShowRows && !isLoading;
    const renderEmptyOrNull = showEmptyState ? <InvoiceEmptyState colSpan={6} /> : null;

    return (
        <Table className="table keyGenerate">
            <InvoiceTableHeader filterData={filterData} onSortClick={onSortClick} />
            <tbody>
                {shouldShowRows ? (
                    invoiceListData.map(invoice => (
                        <InvoiceTableRow
                            key={invoice.id}
                            invoice={invoice}
                            onAction={onRowAction}
                            onDownloadPDF={onDownloadPDF}
                            isDownloading={downloadingInvoiceId === invoice.id}
                        />
                    ))
                ) : renderEmptyOrNull}
            </tbody>
        </Table>
    );
};

InvoiceTable.propTypes = {
    invoiceListData: PropTypes.array.isRequired,
    filterData: PropTypes.shape({
        sortColumn: PropTypes.string,
        sortOrder: PropTypes.string,
    }).isRequired,
    onRowAction: PropTypes.func.isRequired,
    onSortClick: PropTypes.func.isRequired,
    onDownloadPDF: PropTypes.func,
    downloadingInvoiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    isLoading: PropTypes.bool,
};

/**
 * InvoiceCounter - Invoice counter section showing item range
 * Displays: "Showing X-Y of Z invoices"
 * @param {Array} invoiceListData - Array of invoice objects
 * @param {Object} filterData - Current filter state (pageIndex, pageSize)
 * @param {Function} setfilterData - Function to update filter state
 */
export const InvoiceCounter = ({ invoiceListData, filterData, setfilterData }) => {
    return (
        <div
            data-testid="invoice-counter"
            style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '15px',
                paddingBottom: '10px',
                borderBottom: '1px solid #eee',
            }}
        >
            <span style={{ fontSize: '14px', color: '#666' }}>
                Showing {Math.max(1, (filterData.pageIndex - 1) * filterData.pageSize + 1)}-
                {Math.min(invoiceListData.length, filterData.pageIndex * filterData.pageSize)} of{' '}
                {invoiceListData.length} invoices
            </span>
        </div>
    );
};

InvoiceCounter.propTypes = {
    invoiceListData: PropTypes.array.isRequired,
    filterData: PropTypes.shape({
        pageIndex: PropTypes.number.isRequired,
        pageSize: PropTypes.number.isRequired,
    }).isRequired,
    setfilterData: PropTypes.func.isRequired,
};

/**
 * InvoicePagination - Pagination section with page controls
 * @param {Array} invoiceListData - Array of invoice objects
 * @param {Object} filterData - Current filter state (pageIndex, pageSize)
 * @param {Function} setfilterData - Function to update filter state
 * @param {number} totalRecords - Total number of records available (from API)
 */
export const InvoicePagination = ({ invoiceListData, filterData, setfilterData, totalRecords = 0 }) => {
    // Import numOfPage from constant at top of InvoicesList
    return (
        <BatchPagination
            gridData={invoiceListData}
            totalVisualRecords={invoiceListData?.length}
            numberOfPage={filterData.pageSize}
            numOfPage={[10, 20, 30, 50]}
            loader={false}
            currentPage={filterData.pageIndex}
            totalPages={Math.ceil((totalRecords || invoiceListData?.length) / filterData.pageSize)}
            totalRecords={totalRecords || invoiceListData?.length}
            setCurrentPage={value => setfilterData({ ...filterData, pageIndex: value })}
            setNumberOfPage={value => setfilterData({ ...filterData, pageSize: value })}
        />
    );
};

InvoicePagination.propTypes = {
    invoiceListData: PropTypes.array.isRequired,
    filterData: PropTypes.shape({
        pageIndex: PropTypes.number.isRequired,
        pageSize: PropTypes.number.isRequired,
    }).isRequired,
    setfilterData: PropTypes.func.isRequired,
    totalRecords: PropTypes.number,
};
